// v0.6.2: Live Feed panel for the public board.
//
// The existing 'just_shipped' panel shows a static list of the
// 8 most recent pr.closed + quest.completed events. That's
// fine for a wall TV that rotates every 5s, but a dedicated
// "live feed" panel can:
//   - include all event types (transitions, comments, kudos, etc.)
//   - show 20 events at a time
//   - animate new events in at the top with a staggered fade
//   - slide the oldest off the bottom when a 21st arrives
//   - surface the XP + coin total per event so the wall TV
//     shows "Rick · YOLO-6517 · closed a bug! earning +10 XP
//     and +2 🪙" rather than a dry list
//
// The shape mirrors the player dashboard's Recent events feed
// (jira · YOLO-1234 · <rule description>) so the wall TV and
// the player dashboard read the same way for the same event.

import { useEffect, useRef, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface BoardUser { handle: string; name: string | null; }

import { isSignificantEvent } from './significance';

export interface LiveFeedEvent {
  id: string;
  when: string;            // ISO timestamp
  type: string;            // event type (e.g. 'issue.transition', 'pr.closed')
  target: string | null;   // external id (YOLO-1234, #NN, etc.)
  who: BoardUser;
  payload: Record<string, unknown>;
  rule_description: string | null;
  xp: number;
  coins: number;
  // v0.8.8: whether this event's issue is part of an active goal
  // and/or an active boss. Drives the "goal" / "boss" chips on the row.
  //
  // 'adventure' is the campaign set - committed tickets plus goal tickets - and
  // drives the silver rule on the right edge. Deliberately separate from 'boss':
  // a boss can cover an epic that is not part of this campaign.
  contexts?: Array<'goal' | 'boss' | 'adventure'>;
}

interface LiveFeedResponse {
  events: LiveFeedEvent[];
  // M5.11: per-team live_feed_timeout_minutes from the
  // team's public_board_config (default 60). Surfaced
  // on the wall TV so viewers can tell at a glance
  // whether the feed shows "the last 5 minutes" or
  // "the last 24 hours". Clamped server-side to
  // [1, 10080] (1 min..1 week).
  live_feed_timeout_minutes: number;
  // v0.6.2: per-team display color for the live feed
  // row backgrounds. The api falls back to a muted
  // green (DEFAULT_LIVE_FEED_ITEM_COLOR) when the
  // value is missing or invalid, so this is always
  // present. The web client uses the [r, g, b] as
  // the base hue and computes a per-row gradient:
  // newest rows get a light tint of the color,
  // oldest rows get a saturated, deeper version
  // (the "more opaque" effect the operator asked
  // for — reads as "fading into the team's color
  // as the event ages out of the live window").
  live_feed_item_color: [number, number, number];
}

const MAX_VISIBLE = 20;
const FADE_IN_MS = 600;
const STAGGER_MS = 80;
const REFETCH_MS = 2_000;

export function LiveFeedPanel() {
  // Refetch aggressively (every 2s). The panel's whole
  // reason to exist is the "events as they happen" feel,
  // so 5s (the rest of the board's rotation) is too slow.
  // The query is independent of the parent /api/public-board
  // query so the wall TV can rotate through panels without
  // resetting the live feed's animation state.
  const { data } = useQuery<LiveFeedResponse>({
    queryKey: ['public-board-live-feed'],
    queryFn: () => api<LiveFeedResponse>('/api/public-board/live-feed'),
    refetchInterval: REFETCH_MS,
  });

  // Maintain a local visible list. New events are
  // prepended, old events beyond MAX_VISIBLE are dropped.
  // The `seenIds` set prevents re-adding the same event
  // when the refetch returns a stable list (a refetch
  // every 2s without new events should NOT trigger a
  // re-animation storm).
  const [visible, setVisible] = useState<LiveFeedEvent[]>([]);
  const seenIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!data?.events) return;
    const incoming = data.events;
    // Server returns newest first. New IDs that aren't
    // already visible get prepended; existing IDs are
    // left in place (no re-ordering, no re-animation).
    // The prepended batch is the "new events to animate".
    const newOnes: LiveFeedEvent[] = [];
    for (const ev of incoming) {
      if (!seenIds.current.has(ev.id)) {
        seenIds.current.add(ev.id);
        newOnes.push(ev);
      }
    }
    if (newOnes.length === 0) return;
    setVisible((prev) => {
      const combined = [...newOnes, ...prev];
      // Dedupe by id (in case the server changed
      // its ordering between polls — shouldn't
      // happen but defensive).
      const dedup = new Map<string, LiveFeedEvent>();
      for (const e of combined) dedup.set(e.id, e);
      const arr = Array.from(dedup.values()).slice(0, MAX_VISIBLE);
      // Cap seenIds at 100 to prevent unbounded growth
      // during long wall-TV sessions.
      if (seenIds.current.size > 100) {
        const keep = new Set(arr.map((e) => e.id));
        seenIds.current = keep;
      }
      return arr;
    });
  }, [data]);

  const reduceMotion = usePrefersReducedMotion();

  return (
    <div className="flex h-full flex-col">
      {/*
        M5.11: small banner at the top of the panel
        showing the live feed's time window. Default is
        60 minutes (set in DEFAULT_LIVE_FEED_TIMEOUT_MIN
        on the api side). The wall TV viewer can tell
        at a glance whether the feed shows "the last 5
        minutes" (hyper-live) or "the last 24 hours"
        (today's work). Adjustable per team via
        /admin/public-board (the input already lives
        there; this banner just makes the value visible
        to viewers).

        Format rules:
          - < 60 min: "Last Xm"  (e.g. "Last 5m")
          - 60-119 min: "Last 1h"
          - >= 120 min: round to nearest hour
            (e.g. 150 min -> "Last 3h", 1440 min -> "Last 24h")
        We use the api value as the source of truth; if
        it ever disagrees with the panel's local
        expectation, the api wins.
       */}
      {data?.live_feed_timeout_minutes != null ? (
        <p className="mb-1.5 text-xs text-ink-dim">
          Showing the {formatLiveFeedWindow(data.live_feed_timeout_minutes)}.
        </p>
      ) : null}
      <FeedList
        events={visible}
        reduceMotion={reduceMotion}
        itemColor={data?.live_feed_item_color ?? null}
        timeoutMin={data?.live_feed_timeout_minutes ?? null}
      />
    </div>
  );
}

// The visible list. New events (those without the
// 'is-entered' class) get a staggered fade-in + slide-
// from-top animation. The 'is-entered' class is added
// after the animation finishes so subsequent re-renders
// (e.g. on poll) don't re-trigger the animation.
//
// v0.6.2: each row's background fades from a light
// tint of the team's configured color (newest, at the
// top) to a saturated, deep version (oldest, at the
// bottom). The fade is keyed off the event's age
// relative to the team's live_feed_timeout_minutes
// rather than the row's position in the list, so a
// fast-burst team doesn't get an all-pale batch and a
// slow team doesn't get an all-dark one. When
// itemColor is null (api hasn't responded yet) the row
// falls back to the default dark panel background.
function FeedList({
  events,
  reduceMotion,
  itemColor,
  timeoutMin,
}: {
  events: LiveFeedEvent[];
  reduceMotion: boolean;
  itemColor: [number, number, number] | null;
  timeoutMin: number | null;
}) {
  // Track which events have finished their entrance animation.
  // We mark an event as 'entered' once its animation has had
  // time to run. The CSS keyframe animation runs once on
  // mount; the 'entered' class prevents the staggered delay
  // from re-applying on subsequent polls.
  const [entered, setEntered] = useState<Set<string>>(new Set());
  useEffect(() => {
    // Schedule the 'entered' mark for each new event after
    // its animation completes. The longest delay is
    // (MAX_VISIBLE - 1) * STAGGER_MS + FADE_IN_MS = 2.16s,
    // so a single 2.5s timer covers the whole batch.
    if (events.length === 0) return;
    const t = setTimeout(() => {
      setEntered((prev) => {
        const next = new Set(prev);
        for (const e of events) next.add(e.id);
        return next;
      });
    }, Math.max(0, (events.length - 1) * STAGGER_MS) + FADE_IN_MS + 100);
    return () => clearTimeout(t);
  }, [events]);

  // Compute the recency fraction for each event based on
  // its `when` timestamp relative to NOW() and the
  // team's timeout window. 0 = just arrived (brightest);
  // 1 = right at the edge of the window (deepest color).
  // Clamped to [0, 1] so events slightly outside the
  // window (shouldn't happen — the api filters them)
  // still get the deepest color rather than a number
  // > 1 that would extrapolate into illegible territory.
  const recencyById = useMemo(() => {
    const m = new Map<string, number>();
    if (timeoutMin == null || timeoutMin <= 0) {
      // No timeout known yet — fall back to position-
      // based fade (newer = lighter). This keeps the
      // panel usable during the first paint before
      // the live feed query resolves.
      for (let i = 0; i < events.length; i++) {
        m.set(events[i]!.id, events.length <= 1 ? 0 : i / (events.length - 1));
      }
      return m;
    }
    const now = Date.now();
    const windowMs = timeoutMin * 60_000;
    for (const ev of events) {
      const ageMs = Math.max(0, now - new Date(ev.when).getTime());
      m.set(ev.id, Math.min(1, ageMs / windowMs));
    }
    return m;
  }, [events, timeoutMin]);

  // Reverse so newest is at the top (index 0 in CSS terms).
  // CSS `order` handles the visual stacking; we map
  // `events` to li elements with `key={id}` and let the
  // keyframe animation handle the entrance.
  //
  // v0.8.9: the list is now scrollable when the feed is
  // longer than the viewport. flex-1 + min-h-0 lets it
  // claim the remaining height of the panel and scroll
  // internally; hide-scrollbar suppresses the visible
  // scrollbar so the wall-TV / public-board look stays
  // clean. Previously overflow-hidden clipped the extra
  // rows with no way to reach them.
  return (
    <ul className="hide-scrollbar flex flex-1 min-h-0 flex-col gap-1.5 overflow-y-auto">
      {events.map((ev, i) => {
        const isEntered = entered.has(ev.id);
        const recency = recencyById.get(ev.id) ?? 0;
        return (
          <li
            key={ev.id}
            className={isEntered || reduceMotion ? '' : 'mirabili-livefeed-enter'}
            style={
              isEntered || reduceMotion
                ? undefined
                : { animationDelay: `${i * STAGGER_MS}ms` }
            }
          >
            <FeedRow event={ev} itemColor={itemColor} recency={recency} />
          </li>
        );
      })}
      {events.length === 0 ? (
        <li className="rounded-lg bg-bg-panel p-6 text-2xl text-ink-dim">
          No events in the {formatLiveFeedWindow(timeoutMin ?? 60)}.
        </li>
      ) : null}
    </ul>
  );
}

// A single row. Renders the same event-title shape as the
// player dashboard's Recent events feed, with the XP and
// coin totals appended so the wall TV can read
// "Rick · YOLO-6517 · closed a bug! earning +10 XP and +2 🪙".
//
// v0.6.2: each row's background is a shade of the team's
// configured live_feed_item_color, with the lightness
// and saturation scaled by the event's recency. Newest
// (recency ~0) is a light tint; oldest (recency ~1) is
// a saturated, deep version. The text stays the default
// ink color so it remains readable on every shade. When
// itemColor is null (api hasn't responded yet), the
// row keeps the legacy dark panel background so the
// panel is still useful before the first paint.
function FeedRow({
  event,
  itemColor,
  recency,
}: {
  event: LiveFeedEvent;
  itemColor: [number, number, number] | null;
  recency: number;
}) {
  const { displayName, idText } = useMemo(() => {
    const name = event.who.name ?? '@' + event.who.handle;
    return { displayName: name, idText: pickEventId(event) };
  }, [event]);
  const description = event.rule_description ?? humanType(event.type);
  // "earning X XP and Y coins" — only the non-zero halves
  // are shown so "earning +10 XP" doesn't trail "and 0 coins".
  // For events with 0 XP and 0 coins (kudos given, manual
  // events) we still show the title without the "earning"
  // clause — the activity is interesting on its own.
  const hasAward = event.xp > 0 || event.coins > 0;
  const xpPart = event.xp > 0 ? `+${event.xp} XP` : null;
  const coinPart = event.coins > 0 ? `${event.coins} 🪙` : null;
  const earning = hasAward ? ' earning ' : '';
  const awardText = [xpPart, coinPart].filter(Boolean).join(' and ');
  const contexts = event.contexts ?? [];
  // v0.6.2: the per-row background uses the team's
  // color at varying alpha. Newest row = nearly
  // opaque (the team's full color, "just happened"
  // and visually loud); oldest row = very transparent
  // (a faint tint, the event is about to time out).
  // The operator only sets the color in
  // /admin/public-board; the alpha gradient is baked
  // into the live feed panel and is not admin-tunable.
  // The direction is "newest is brighter": the wall
  // TV reads the live feed from the top, and the
  // loudest row is the one the operator just sent.
  const bg = itemColor ? rowBackground(itemColor, recency) : undefined;
  // PROGRESS IS LOUDER THAN ACTIVITY.
  //
  // Gold on the row FILL, not on the words: the name and the description stay
  // default ink so they stay legible, and the row announces itself behind them.
  //
  // It replaces the recency tint rather than layering over it. One meaning per
  // row: gold means this finished, the team colour means it is ordinary
  // activity fading with age. Two tints on one background would be two meanings
  // on one channel, which is how a legend stops being true.
  //
  // Semi-transparent on purpose. Solid gold reads as a selection state, or as a
  // warning banner; a wash reads as emphasis, and keeps the dark ink text
  // readable over it.
  const significant = isSignificantEvent(event);
  const SIG_BG = 'rgba(251, 191, 36, 0.20)';
  const rowBg = significant ? SIG_BG : bg;

  // IN THE CAMPAIGN: a rule down the RIGHT edge, mirroring the gold one on the
  // left. Two edges, two independent facts - it FINISHED (left, gold) and it is
  // CAMPAIGN WORK (right, silver) - so a row can carry either, both or neither
  // without either meaning being diluted.
  //
  // Structure rather than colour on purpose. Gold is spoken for by done/closed,
  // the background by recency, and every other hue in the palette already means
  // something. An edge says it without spending a colour.
  //
  // Silver, and the same weight as the gold rule. It was deliberately thinner at
  // first, so the left edge would win the eye; that made it too easy to miss, and
  // two equal edges read as a pair rather than as an accent and an accident.
  // 'adventure' covers committed tickets AND goal tickets, unioned in the API.
  // Boss keys are deliberately NOT included: a boss can cover an epic that is
  // not part of this campaign.
  //
  // KNOWN GAP: this reads contexts off the event's issue key, and PR events do
  // not carry one, so a PR belonging to campaign work is not marked.
  const inAdventure = event.contexts?.includes('adventure') ?? false;
  const rowClass = [
    'rounded-lg px-3 py-2',
    significant ? 'border-l-4 border-amber-400/70 pl-2' : '',
    inAdventure ? 'border-r-4 border-zinc-300/70 pr-2' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={rowClass}
      style={rowBg ? { backgroundColor: rowBg } : undefined}
    >
      <p className="text-lg font-semibold text-ink">
        <span>{displayName}</span>
        <span className="mx-2 text-ink-dim">·</span>
        <span className="font-mono text-base text-ink">{idText}</span>
        <span className="mx-2 text-ink-dim">·</span>
        <span>{description}</span>
        {contexts.includes('goal') && (
          <span className="ml-2 inline-block rounded-full border border-amber-300/40 bg-amber-400/10 px-1.5 py-0.5 text-[0.65em] uppercase tracking-wide text-amber-300 align-middle">
            goal
          </span>
        )}
        {contexts.includes('boss') && (
          <span className="ml-1 inline-block rounded-full border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[0.65em] uppercase tracking-wide text-red-400 align-middle">
            boss
          </span>
        )}
        {hasAward ? (
          <>
            <span className="ml-2 text-ink-dim">{earning}</span>
            <span className="text-accent-success">{awardText}</span>
          </>
        ) : null}
      </p>
    </div>
  );
}

// The same pickEventId logic the player dashboard uses, so
// the wall TV and the player's Recent feed read the same
// way for the same event. Issue key for Jira, '#NN' for
// GitHub, quest title for quests, target_external for
// everything else.
function pickEventId(e: LiveFeedEvent): string {
  const p = e.payload ?? {};
  // v0.6.2: for Jira issues and GitHub PRs, append the
  // comment_id / review_id when present so the wall TV
  // shows distinct rows for distinct events. Without
  // this, three comments on YOLO-6627 render as three
  // 'YOLO-6627' rows and the operator reading the wall
  // assumes they are duplicates. They aren't — each
  // comment_id is the unique external_id suffix and
  // the dedup key is (event_id, rule_id, competency_id).
  // The 'c' prefix stands for 'comment', 'r' for
  // 'review' (visible inline on the wall TV).
  const issueKey = typeof p.issue_key === 'string' ? p.issue_key : null;
  if (issueKey) {
    const commentId = typeof p.comment_id === 'string' && p.comment_id.length > 0 ? p.comment_id : null;
    return commentId ? `${issueKey} · c${commentId}` : issueKey;
  }
  const prNumber = typeof p.pr_number === 'number' ? p.pr_number : null;
  if (prNumber !== null) {
    const reviewId = typeof p.review_id === 'string' && p.review_id.length > 0 ? p.review_id : null;
    return reviewId ? `#${prNumber} · r${reviewId}` : `#${prNumber}`;
  }
  const questTitle = typeof p.quest_title === 'string' ? p.quest_title : null;
  if (questTitle) return questTitle;
  if (e.target) return e.target;
  return humanType(e.type);
}

// A human-readable version of the event type for events
// that have no rule description. "issue.transition" →
// "Issue transition", "pr.opened" → "PR opened", etc.
// Used as the trailing token in the row title when the
// rule has no description.
function humanType(t: string): string {
  const map: Record<string, string> = {
    'pr.opened': 'PR opened',
    'pr.closed': 'PR closed',
    'pr.ready_for_review': 'PR ready for review',
    'pr.review_submitted': 'PR review submitted',
    'issue.created': 'Issue created',
    'issue.commented': 'Issue commented',
    'issue.transition': 'Issue transition',
    'issue.updated': 'Issue updated',
    'quest.completed': 'Quest completed',
    'kudos.given': 'Kudos given',
    'manual.grant': 'Admin grant',
  };
  return map[t] ?? t;
}

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduce(mq.matches);
    setReduce(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

// M5.11: human-readable format for the live feed
// time window. The api clamps to [1, 10080] so the
// input is always a sensible number of minutes.
//
// Examples:
//   5     -> "last 5m"
//   59    -> "last 59m"
//   60    -> "last 1h"
//   119   -> "last 2h"   (rounded)
//   120   -> "last 2h"
//   150   -> "last 3h"   (rounded to nearest)
//   1440  -> "last 24h"
//   10080 -> "last 168h" (= 1 week; rare; kept as
//            hours for simplicity rather than
//            introducing days/weeks)
export function formatLiveFeedWindow(minutes: number): string {
  const m = Math.max(1, Math.round(minutes));
  if (m < 60) return `last ${m}m`;
  const hours = Math.round(m / 60);
  return `last ${hours}h`;
}

// v0.6.2: per-row background for the live feed.
// The admin picks a single color in
// /admin/public-board; the panel derives a per-row
// transparency from the event's recency (0 = just
// arrived, 1 = at the timeout edge).
//
// v0.6.2 (revised): piecewise constant alpha in 6
// bands. The operator wanted more than 3 visual
// ranges — a single linear lerp (newest 0.95 →
// oldest 0.15) only gives three meaningfully
// different shades at a glance (new / mid / old).
// 6 bands give the wall TV more visual structure
// without introducing an admin-tunable knob for
// transparency. Each band has a constant alpha,
// so the visual difference between adjacent rows
// comes from the band boundaries, not smooth
// interpolation.
//
// Band table (recency 0 = newest, 1 = oldest):
//   band 0 (newest,    0.00 - 0.17) → 0.95 (full)
//   band 1 (very fresh, 0.17 - 0.33) → 0.85
//   band 2 (fresh,     0.33 - 0.50) → 0.70
//   band 3 (mid,       0.50 - 0.67) → 0.55
//   band 4 (fading,    0.67 - 0.83) → 0.35
//   band 5 (oldest,    0.83 - 1.00) → 0.15 (faint)
//
// 6 bands, 6 distinct alpha values. The operator
// can read the recency at a glance: a row's
// saturation tells them how fresh the event is.
// Newest is the loudest (full color, 0.95);
// oldest is the quietest (faint tint, 0.15).
//
// Example (admin picks the default green [34, 197, 94]):
//   recency 0.00 → rgba(34, 197, 94, 0.95)   (full color)
//   recency 0.20 → rgba(34, 197, 94, 0.85)
//   recency 0.40 → rgba(34, 197, 94, 0.70)
//   recency 0.55 → rgba(34, 197, 94, 0.55)
//   recency 0.75 → rgba(34, 197, 94, 0.35)
//   recency 0.95 → rgba(34, 197, 94, 0.15)   (faint tint)
//
// The 0.15 floor ensures even the oldest row is
// at least faintly visible (a fully-transparent
// row would be invisible on the dark panel
// background and the operator couldn't see it
// age out).
//
// The exported name is preserved (rowBackground)
// so the admin settings preview can import the
// same helper and render an accurate preview.
const RECENCY_BANDS: ReadonlyArray<{ from: number; alpha: number }> = [
  { from: 0.00, alpha: 0.95 },
  { from: 0.17, alpha: 0.85 },
  { from: 0.33, alpha: 0.70 },
  { from: 0.50, alpha: 0.55 },
  { from: 0.67, alpha: 0.35 },
  { from: 0.83, alpha: 0.15 },
];

export function rowBackground(
  rgb: [number, number, number],
  recency: number,
): string {
  // v0.6.2: clamp the recency fraction. A future
  // caller (or a buggy one) might pass a value > 1
  // if the math drifts above the timeout (e.g. the
  // api returns a 0-minute timeout or the wall TV
  // clock skews). Clamping keeps the alpha output
  // well-formed — values stay in the band table,
  // the gradient doesn't extrapolate, and the wall
  // TV never renders an invalid color.
  const t = Math.max(0, Math.min(1, recency));
  // Walk the band table from the top (newest).
  // The first band whose `from` <= t wins. The
  // table is short (6 entries) and sorted by
  // `from` ascending, so a linear scan is fine
  // (an O(log n) bisect would be premature).
  // The final band has from=0.83 and the loop
  // returns the last entry as the default.
  let alpha = RECENCY_BANDS[RECENCY_BANDS.length - 1]!.alpha;
  for (const band of RECENCY_BANDS) {
    if (t >= band.from) {
      alpha = band.alpha;
      // Don't break — the table is sorted ascending
      // by `from`; the LAST matching band is the
      // correct one (highest `from` that's still
      // <= t). Iterating to the end is the only way
      // to find it without reversing the table.
    }
  }
  const [r, g, b] = rgb;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
